import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Printer, ArrowLeft, Eye, EyeOff, ShieldAlert, KeyRound, Building2, CreditCard, MessageSquare, Users, Map, Wrench, Star, LifeBuoy, Bell, DollarSign, FileText, AlertCircle, CheckCircle2, XCircle, Loader } from "lucide-react";

export default function OperationsManual() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [config, setConfig] = useState(null);
  const [revealSecrets, setRevealSecrets] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/system-config");
        setConfig(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Only admins can view this manual.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-asap-deep font-display uppercase">Loading manual…</div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center flex-col gap-4 p-8 text-center">
      <ShieldAlert size={48} className="text-asap-red"/>
      <div className="font-display text-2xl uppercase text-asap-deep">Access Denied</div>
      <div className="text-slate-600">{err}</div>
      <button onClick={() => nav("/login")} className="px-4 py-2 bg-asap-yellow text-asap-deep border-2 border-asap-deep font-bold uppercase text-sm">Log in as admin</button>
    </div>
  );

  const printNow = () => window.print();

  return (
    <div className="min-h-screen bg-slate-50 manual-root" data-testid="operations-manual">
      {/* Screen-only top bar */}
      <div className="no-print bg-asap-deep text-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center gap-3 flex-wrap">
          <button onClick={() => nav("/admin")} className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider hover:text-asap-yellow"><ArrowLeft size={14}/> Back to Admin</button>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setRevealSecrets(!revealSecrets)}
              data-testid="reveal-toggle"
              className="inline-flex items-center gap-2 px-3 py-2 bg-asap-yellow text-asap-deep font-bold text-xs uppercase tracking-wider border-2 border-asap-yellow hover:bg-white transition"
            >
              {revealSecrets ? <><EyeOff size={12}/> Hide Secrets</> : <><Eye size={12}/> Reveal Secrets</>}
            </button>
            <button
              onClick={printNow}
              data-testid="print-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-asap-red text-white font-display uppercase text-sm border-2 border-asap-red shadow-hard-sm hover:translate-x-[1px] hover:translate-y-[1px] transition-all"
            >
              <Printer size={14}/> Save as PDF
            </button>
          </div>
        </div>
      </div>

      {/* Manual content — print-friendly */}
      <div className="max-w-4xl mx-auto px-8 py-10 space-y-10 manual-content">
        <Header user={user}/>

        <Section id="section-quickstart" icon={CheckCircle2} title="1. Quick-Start (60 seconds)">
          <ol className="list-decimal ml-6 space-y-2 text-slate-700">
            <li><strong>Log in</strong> at <code>/login</code> with your admin email (see Section 2 for credentials).</li>
            <li>You land on the <strong>Admin Dashboard</strong>. Six tabs: Overview · Bookings · Ops · Users · Zones · Tickets · Tiers · AI/Webhooks.</li>
            <li><strong>Ops tab</strong> is where you approve drivers, manually dispatch, and process refunds.</li>
            <li><strong>Bookings tab</strong> is your entire revenue and job history.</li>
            <li><strong>Zones tab</strong> is where you draw service areas and unlock founding driver slots.</li>
            <li>To add a helper: <strong>Ops → Manage Admins → Invite New Admin</strong> (Section 6).</li>
          </ol>
        </Section>

        <Section id="section-creds" icon={KeyRound} title="2. Your Credentials & Master Access">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <KV k="Admin Email" v={config?.auth.admin_email} testid="cfg-admin-email"/>
            <KV k="Admin Password" v={revealSecrets ? "MalachiTayEvie#21" : "•••••••••••••••••"} testid="cfg-admin-pw"/>
            <KV k="Preview URL" v={config?.app.preview_url}/>
            <KV k="Custom Domain" v={config?.app.custom_domain} sub="Pending DNS setup"/>
          </div>
          <Callout tone="warn" title="Critical — Rotate These After Any Staff Change">
            If someone leaves your team, immediately change ADMIN_PASSWORD in backend/.env and redeploy. Also revoke their individual admin login in the Ops → Manage Admins panel.
          </Callout>
        </Section>

        <Section id="section-keys" icon={CreditCard} title="3. API Keys & Third-Party Accounts">
          <SubHead>Stripe — Payments <Badge tone={config?.stripe.mode === "LIVE" ? "danger" : "info"}>{config?.stripe.mode}</Badge></SubHead>
          <div className="grid sm:grid-cols-2 gap-4">
            <KV k="Secret Key (sk_live_...)" v={revealSecrets ? config?.stripe.api_key_full : config?.stripe.api_key_masked} testid="cfg-stripe-key"/>
            <KV k="Dashboard" v={<a className="text-asap-orange underline" href={config?.stripe.dashboard_url} target="_blank" rel="noreferrer">dashboard.stripe.com</a>}/>
            <KV k="Webhook Endpoint" v={config?.stripe.webhook_endpoint} sub="Configure in Stripe Dashboard → Developers → Webhooks"/>
            <KV k="What's Set Up" v="Checkout Sessions, Refunds recorded, Tips 100% to driver"/>
          </div>

          <SubHead>Twilio — SMS <Badge tone={config?.twilio.status?.includes("NOT") ? "danger" : "warn"}>{config?.twilio.status}</Badge></SubHead>
          <div className="grid sm:grid-cols-2 gap-4">
            <KV k="Account SID" v={config?.twilio.account_sid || "—"} testid="cfg-twilio-sid"/>
            <KV k="Auth Token" v={revealSecrets ? config?.twilio.auth_token_full : config?.twilio.auth_token_masked} testid="cfg-twilio-token"/>
            <KV k="From Number" v={config?.twilio.from_number || "—"}/>
            <KV k="Console" v={<a className="text-asap-orange underline" href={config?.twilio.console_url} target="_blank" rel="noreferrer">console.twilio.com</a>}/>
            <KV k="Inbound Webhook" v={config?.twilio.inbound_webhook} sub="For STOP / HELP / opt-out compliance"/>
          </div>

          <SubHead>Emergent LLM (AI Universal Key)</SubHead>
          <div className="text-sm text-slate-700 space-y-1">
            <p>Your Emergent Universal Key is provisioned inside the platform. It works for Claude Sonnet (text), OpenAI GPT-5.6 (text + image), Gemini + Nano Banana (text + image), Sora-2 (video), and OpenAI Whisper (STT).</p>
            <p>To manage balance: <strong>Emergent Chat → Profile → Manage Plan → Universal Key</strong>.</p>
          </div>

          <SubHead>Database</SubHead>
          <div className="grid sm:grid-cols-2 gap-4">
            <KV k="Mongo URL" v={revealSecrets ? "mongodb://localhost:27017" : config?.database.mongo_url_masked}/>
            <KV k="DB Name" v={config?.database.db_name || "test_database"}/>
          </div>

          <SubHead>JWT (session signing)</SubHead>
          <KV k="Secret" v={revealSecrets ? "See backend/.env → JWT_SECRET" : config?.auth.jwt_secret_masked} sub="Rotating this logs everyone out — do it after any suspected breach"/>
        </Section>

        <Section id="section-daily" icon={Wrench} title="4. Daily Operations Playbook">
          <p className="text-slate-700 mb-4">A typical morning shift at ASAP Roadside runs like this:</p>
          <Playbook steps={[
            { t: "Log in to Admin", d: "Open /login and sign in. You'll land on the Overview tab with live stats: users, drivers online, active jobs, revenue." },
            { t: "Check Ops → Driver Approval Queue", d: "Any new driver applications from the last 24 hours. Click 'View Docs' to see the license/insurance URLs they uploaded, then Approve or Reject. Rejected drivers get an optional note explaining why." },
            { t: "Ops → Manual Dispatch — Unclaimed Requests", d: "If any customer request has been pending too long (auto-dispatch missed a driver), pick a capable driver from the dropdown. Only drivers whose vehicles can perform that service will show up." },
            { t: "Ops → Refunds", d: "Any customer complaints? Click Refund on the row. This RECORDS the refund internally — you must ALSO complete the money-return in your Stripe Dashboard → Payments." },
            { t: "Tickets tab", d: "Answer any support tickets in the queue. Filter by priority. Mark resolved when done. Customer sees your replies in their in-app support drawer." },
            { t: "Zones tab", d: "Watch the founding-slot fill rate (7 per 9-mile zone). Once a zone hits 7, new drivers must subscribe ($29/mo) — toggle that via Users tab or the driver's admin sub panel." },
            { t: "End of day", d: "Overview tab. Screenshot revenue + completed jobs count for your records. Any SLA breaches (bookings pending > 10 min) trigger AI alerts." },
          ]}/>
        </Section>

        <Section id="section-drivers" icon={Users} title="5. Driver Lifecycle Management">
          <SubHead>Onboarding a new driver</SubHead>
          <ol className="list-decimal ml-6 space-y-1 text-slate-700 text-sm">
            <li>Driver signs up at <code>/register</code>, selects "I'm a driver".</li>
            <li>They accept ToS + Privacy + Driver Agreement (consent gate — legally required).</li>
            <li>They must upload docs in <strong>Driver Dashboard → Onboarding Docs</strong> panel: Driver's License, Auto Insurance (COI), Vehicle Registration.</li>
            <li>Their <code>approval_status</code> flips to <strong>pending_review</strong>.</li>
            <li>You see them in <strong>Admin → Ops → Driver Approval Queue</strong>.</li>
            <li>You click <strong>Approve</strong> after verifying: driver license matches state, insurance is active with them as covered driver, vehicle is registered to them or has a corporate assignment letter.</li>
            <li>Now they can pick a service zone and go online.</li>
          </ol>

          <SubHead>Driver Tiers (Auto-Promotion)</SubHead>
          <table className="w-full text-sm mt-2 border-2 border-slate-200">
            <thead className="bg-slate-100">
              <tr className="text-left">
                <th className="p-2">Tier</th><th className="p-2">Min Jobs</th><th className="p-2">Min Rating</th><th className="p-2">Multiplier</th><th className="p-2">Driver Take</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t"><td className="p-2 font-bold text-amber-700">Bronze</td><td className="p-2">0</td><td className="p-2">Any</td><td className="p-2">1.00×</td><td className="p-2">70% base</td></tr>
              <tr className="border-t"><td className="p-2 font-bold text-slate-500">Silver</td><td className="p-2">10</td><td className="p-2">4.5★</td><td className="p-2">1.10×</td><td className="p-2">77% base</td></tr>
              <tr className="border-t"><td className="p-2 font-bold text-yellow-600">Gold</td><td className="p-2">50</td><td className="p-2">4.7★</td><td className="p-2">1.20×</td><td className="p-2">84% base</td></tr>
            </tbody>
          </table>
          <div className="text-xs text-slate-500 mt-2">Ratings recompute on every customer rating. Tiers auto-promote — no manual work needed.</div>

          <SubHead>Founding vs Subscription drivers</SubHead>
          <p className="text-sm text-slate-700">The <strong>first 7 drivers per 9-mile zone</strong> are "Founding" — free forever. Drivers #8+ must have <code>subscription_active=true</code> ($29/mo). Toggle in <strong>Users tab → Driver row → Subscription button</strong> or via <code>POST /api/admin/driver/subscription</code>.</p>
        </Section>

        <Section id="section-admins" icon={Building2} title="6. Adding & Delegating to Other Admins">
          <p className="text-slate-700 mb-3">You are not stuck being the only admin. Go to <strong>Admin → Ops → Manage Admins</strong>. Two ways to grant access:</p>
          <ol className="list-decimal ml-6 space-y-2 text-slate-700 text-sm">
            <li><strong>Invite New Admin</strong> — creates a brand-new admin account with a temporary password. You share the login credentials with them privately (text, in-person, encrypted email — never in plain email). They can change their password later via Forgot Password.</li>
            <li><strong>Promote Existing User</strong> — if a driver or customer already exists on the platform, promote them to admin without creating a new account. Their existing history stays intact.</li>
          </ol>
          <SubHead>Revoking Admin Access</SubHead>
          <p className="text-sm text-slate-700">Any admin (except the seeded root and yourself) can be revoked with one click. They immediately drop to <code>role=customer</code> — they can still log in to their personal customer account, but lose all admin panels.</p>
          <Callout tone="info" title="Audit Trail">
            Every admin action (approve, reject, refund, assign, revoke) is logged in <code>db.audit_log</code>. You can query it via <code>GET /api/admin/audit</code> or Ops tab.
          </Callout>
        </Section>

        <Section id="section-hire" icon={Users} title="7. Hiring & Paying an Admin Assistant">
          <SubHead>Recommended Setup By Stage</SubHead>
          <div className="grid sm:grid-cols-3 gap-3 text-sm">
            <StageCard stage="Just launching" volume="< 30 bookings/wk" hire="1099 contractor · 5-10 hrs/wk" pay="$18-25/hr · Weekly Zelle" cost="$360-1000/mo"/>
            <StageCard stage="Growing" volume="30-150 bookings/wk" hire="Part-time W-2 · 20 hrs/wk" pay="$18-22/hr · Payroll via Gusto ($40/mo)" cost="$1,500-2,000/mo"/>
            <StageCard stage="Scaling" volume="150+ bookings/wk" hire="Full-time W-2 dispatcher" pay="Salary + PTO + health stipend" cost="$3,500-5,500/mo"/>
          </div>
          <SubHead>What to Delegate First</SubHead>
          <ul className="list-disc ml-6 text-sm text-slate-700 space-y-1">
            <li><strong>Driver approvals</strong> — mechanical checklist, low judgment</li>
            <li><strong>Support ticket triage</strong> — reply to low-priority questions, escalate hard ones</li>
            <li><strong>Manual dispatch overrides</strong> — during peak hours</li>
            <li><strong>Refund processing</strong> — after your policy is written down</li>
            <li><strong>Zone monitoring</strong> — watch for empty zones, recruit new drivers</li>
          </ul>
          <SubHead>What to Keep for Yourself</SubHead>
          <ul className="list-disc ml-6 text-sm text-slate-700 space-y-1">
            <li>Stripe / bank / tax decisions</li>
            <li>Driver contract disputes</li>
            <li>Customer legal complaints</li>
            <li>Price changes</li>
            <li>New feature scoping</li>
          </ul>
        </Section>

        <Section id="section-payment" icon={CreditCard} title="8. Payments, Tips & Refunds">
          <SubHead>How money flows</SubHead>
          <ol className="list-decimal ml-6 text-sm text-slate-700 space-y-1">
            <li>Customer books a service. Booking is created with <code>payment_status=unpaid</code>.</li>
            <li>Driver accepts. Customer sees "Pay Now" button.</li>
            <li>Customer taps Pay → Stripe Checkout opens → they optionally add a tip (15/20/25%/custom) → they pay.</li>
            <li>Stripe webhook fires → booking <code>payment_status=paid</code>.</li>
            <li>Driver sees Paid badge on the job. Driver earnings = <strong>base × 70% × tier_multiplier + 100% of tip</strong>.</li>
            <li>Driver's total earnings visible in Driver Dashboard → Tier Card.</li>
          </ol>
          <SubHead>Fees & Splits (current)</SubHead>
          <ul className="list-disc ml-6 text-sm text-slate-700 space-y-1">
            <li>Platform take: 30% of base price (before Stripe fees)</li>
            <li>Driver take: 70% of base × tier multiplier (Bronze 1.00×, Silver 1.10×, Gold 1.20×)</li>
            <li>Tip: 100% to driver, always</li>
            <li>Stripe fee: ~2.9% + $0.30 per transaction (charged to platform take)</li>
          </ul>
          <SubHead>Refunds</SubHead>
          <ol className="list-decimal ml-6 text-sm text-slate-700 space-y-1">
            <li>Go to <strong>Admin → Ops → Refunds table</strong>.</li>
            <li>Click <strong>Refund</strong> on the booking row.</li>
            <li>This marks the booking as refunded in your app.</li>
            <li><strong>YOU MUST ALSO</strong>: log in to <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noreferrer" className="text-asap-orange underline">Stripe Dashboard → Payments</a>, find the session, click Refund, confirm.</li>
            <li>Stripe returns the money to the customer's card within 5-10 business days.</li>
          </ol>
        </Section>

        <Section id="section-zones" icon={Map} title="9. Zone Management & Rollout">
          <p className="text-slate-700 mb-3">Zones control which drivers get dispatched to which requests. Each zone is a 9-mile-radius circle centered wherever you draw it. First 7 drivers per zone are free founding members.</p>
          {(config?.zones || []).length > 0 ? (
            <table className="w-full text-sm border-2 border-slate-200">
              <thead className="bg-slate-100">
                <tr className="text-left"><th className="p-2">Zone</th><th className="p-2">Center</th><th className="p-2">Radius</th><th className="p-2">Founding</th><th className="p-2">Active</th></tr>
              </thead>
              <tbody>
                {config.zones.map((z) => (
                  <tr key={z.id} className="border-t">
                    <td className="p-2 font-bold text-asap-deep">{z.name}</td>
                    <td className="p-2 text-xs">{z.center_lat?.toFixed?.(3)}, {z.center_lng?.toFixed?.(3)}</td>
                    <td className="p-2">{z.radius_miles} mi</td>
                    <td className="p-2">Slots open — check Zones tab</td>
                    <td className="p-2">{z.active ? "✅" : "⏸"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-sm text-slate-500 border-2 border-dashed border-slate-300 p-4">No zones defined yet. Go to <strong>Admin → Zones</strong> and draw your first one on the map.</div>
          )}
        </Section>

        <Section id="section-audit" icon={AlertCircle} title="10. What's Missing / Backlog / Do-Later">
          <SubHead>Blocked on external action (waiting on YOU)</SubHead>
          <ChecklistItem status="pending" title="Point asaproadrunners.com DNS" note="Follow /app/CUSTOM_DOMAIN_SETUP.md instructions to add A/CNAME records at your registrar"/>
          <ChecklistItem status="pending" title="Upgrade Twilio out of Trial" note="Trial mode blocks SMS to non-verified numbers. Upgrade at console.twilio.com — usually ~$15 min balance to start."/>
          <ChecklistItem status="pending" title="Complete Stripe business verification" note="If not done already — Stripe requires business name, EIN/SSN, bank link, ID. Live key is already saved."/>
          <ChecklistItem status="pending" title="Add SendGrid API key" note="For branded email receipts. Get free tier at sendgrid.com → API Keys → paste into backend/.env as SENDGRID_API_KEY, then redeploy."/>

          <SubHead>Backlog — Features not yet built (worth doing, not blocking launch)</SubHead>
          <ChecklistItem status="todo" title="Live driver GPS tracking on customer map" note="Driver phones already ping location every 20s. Just needs a map overlay showing the truck moving."/>
          <ChecklistItem status="todo" title="Stripe Connect for driver instant cash-out" note="Major integration — drivers get their own Stripe account, get paid directly per job."/>
          <ChecklistItem status="todo" title="SendGrid + Twilio auto receipts" note="After payment: SMS + email receipt with rate driver link"/>
          <ChecklistItem status="todo" title="Native mobile shell via Capacitor" note="Capacitor project exists in /app/mobile — needs FCM push wiring + App Store / Play Store submission"/>
          <ChecklistItem status="todo" title="Time-series analytics dashboard" note="Weekly/monthly charts of jobs, revenue, avg response time"/>
          <ChecklistItem status="todo" title="Referral / promo code redemption at checkout" note="Backend endpoint exists — needs UI on customer checkout"/>
          <ChecklistItem status="todo" title="Saved payment methods (Stripe Customer API)" note="Currently every payment is a fresh Checkout Session — for repeat customers, save their card"/>

          <SubHead>Explicitly skipped (doesn't fit roadside)</SubHead>
          <ChecklistItem status="skip" title="Live driver map before requesting" note="Would mislead — a jump-only driver 200ft away won't do your tow. Better: show total online + capable count"/>
          <ChecklistItem status="skip" title="Saved home/work addresses" note="Roadside is where-you-broke-down, not where-you-are-going. Low utility."/>
          <ChecklistItem status="skip" title="Session timeout for inactivity" note="12h JWT + auto-refresh is fine for this use case. Strict timeouts frustrate stranded users."/>
        </Section>

        <Section id="section-emergency" icon={ShieldAlert} title="11. Emergency & Escalation Procedures">
          <SubHead>Customer pressed SOS during active job</SubHead>
          <ol className="list-decimal ml-6 text-sm text-slate-700 space-y-1">
            <li>You'll see a HIGH-PRIORITY ticket in Tickets tab with subject "🚨 SOS from active booking..."</li>
            <li>Call the customer immediately using the phone number on their booking.</li>
            <li>If the customer confirms danger: they should call 911 first, you follow up.</li>
            <li>Contact the assigned driver via their phone number in the Users tab. Ask them to stand down and leave the scene.</li>
            <li>Document everything in the ticket reply. Refund the booking. Save the audit log.</li>
          </ol>
          <SubHead>App is down / customers can't book</SubHead>
          <ol className="list-decimal ml-6 text-sm text-slate-700 space-y-1">
            <li>Check <code>{"{PREVIEW_URL}"}/api/</code> — should return <code>{"{"}"service":"asap-roadside","status":"ok"{"}"}</code></li>
            <li>If down: log into Emergent dashboard → click Redeploy</li>
            <li>Manually take incoming calls on <strong>831-747-7141</strong> and hand-dispatch until app is back</li>
          </ol>
          <SubHead>Data breach suspicion</SubHead>
          <ol className="list-decimal ml-6 text-sm text-slate-700 space-y-1">
            <li>Rotate JWT_SECRET in backend/.env → forces every user to re-login</li>
            <li>Rotate Stripe key: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-asap-orange underline">Stripe → API keys → Roll</a></li>
            <li>File CCPA/GDPR breach notification if user data affected (see /legal → Data Breach)</li>
            <li>Contact legal counsel</li>
          </ol>
        </Section>

        <Section id="section-endpoints" icon={FileText} title="12. API Endpoints Cheat Sheet">
          <div className="text-xs font-mono grid sm:grid-cols-2 gap-x-4 gap-y-1">
            <EndpointRow method="POST" path="/api/auth/login" note="Sign in"/>
            <EndpointRow method="POST" path="/api/auth/register" note="Create account"/>
            <EndpointRow method="POST" path="/api/auth/refresh" note="Renew session"/>
            <EndpointRow method="POST" path="/api/auth/forgot-password" note="Request reset link"/>
            <EndpointRow method="POST" path="/api/auth/reset-password" note="Use reset token"/>
            <EndpointRow method="GET"  path="/api/bookings/my" note="Your trips"/>
            <EndpointRow method="POST" path="/api/bookings" note="Create booking"/>
            <EndpointRow method="POST" path="/api/bookings/:id/rate" note="Rate driver"/>
            <EndpointRow method="POST" path="/api/payments/checkout" note="Start Stripe pay"/>
            <EndpointRow method="POST" path="/api/driver/documents" note="Upload docs"/>
            <EndpointRow method="POST" path="/api/driver/location" note="GPS ping"/>
            <EndpointRow method="GET"  path="/api/driver/earnings" note="Earnings breakdown"/>
            <EndpointRow method="GET"  path="/api/admin/stats" note="Platform stats"/>
            <EndpointRow method="POST" path="/api/admin/admins" note="Invite new admin"/>
            <EndpointRow method="POST" path="/api/admin/bookings/:id/refund" note="Record refund"/>
            <EndpointRow method="POST" path="/api/admin/bookings/:id/assign" note="Manual dispatch"/>
            <EndpointRow method="POST" path="/api/admin/drivers/:id/approval" note="Approve/reject"/>
            <EndpointRow method="GET"  path="/api/admin/audit" note="Audit log"/>
            <EndpointRow method="GET"  path="/api/ai/feed" note="Event feed (X-API-Key)"/>
            <EndpointRow method="GET"  path="/api/ai/summary" note="Live KPIs (X-API-Key)"/>
          </div>
        </Section>

        <Section id="section-contacts" icon={LifeBuoy} title="13. Contacts & Vendor Support">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <KV k="ASAP Roadside Dispatch (Voice)" v={<a className="text-asap-orange underline" href="tel:+18317477141">831-747-7141</a>}/>
            <KV k="ASAP Roadside Dispatch (Text)" v={<a className="text-asap-orange underline" href="sms:+17653301951">765-330-1951</a>}/>
            <KV k="Business Email" v={<a className="text-asap-orange underline" href="mailto:asaproadrunners@rs-asap.com">asaproadrunners@rs-asap.com</a>}/>
            <KV k="Stripe Support" v={<a className="text-asap-orange underline" href="https://support.stripe.com" target="_blank" rel="noreferrer">support.stripe.com</a>}/>
            <KV k="Twilio Support" v={<a className="text-asap-orange underline" href="https://support.twilio.com" target="_blank" rel="noreferrer">support.twilio.com</a>}/>
            <KV k="Emergent Support" v="Use the in-chat support button on emergent.sh"/>
            <KV k="Domain Registrar" v="Where you bought asaproadrunners.com"/>
          </div>
        </Section>

        <footer className="border-t-4 border-asap-deep pt-6 mt-10 text-center text-xs text-slate-500">
          <div className="font-display uppercase text-asap-deep text-sm">Operations Manual · ASAP Our's 24-7 Network Services</div>
          <div className="mt-1">Generated {new Date().toLocaleString()} · Do not share this document outside your operations team.</div>
        </footer>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .manual-root { background: white; }
          .manual-content { padding: 0 !important; max-width: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          section { break-inside: avoid-page; }
          h2 { break-after: avoid; }
        }
      `}</style>
    </div>
  );
}

function Header({ user }) {
  return (
    <header className="border-b-4 border-asap-deep pb-6">
      <div className="flex items-center gap-4">
        <Logo size={96}/>
      </div>
      <div className="mt-4">
        <div className="text-[10px] tracking-[0.4em] uppercase text-asap-orange font-bold">Operations Manual · v1</div>
        <h1 className="font-display text-4xl uppercase text-asap-deep leading-tight mt-1">Everything You Need To Run ASAP Roadside</h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">For {user?.name || "you"} and any admin you delegate to. Keep secure — this contains sensitive account credentials and API keys.</p>
      </div>
    </header>
  );
}

function Section({ id, icon: Icon, title, children }) {
  return (
    <section id={id} className="space-y-4">
      <h2 className="flex items-center gap-3 border-b-2 border-asap-deep pb-2">
        <Icon size={22} className="text-asap-orange"/>
        <span className="font-display text-2xl uppercase text-asap-deep">{title}</span>
      </h2>
      <div>{children}</div>
    </section>
  );
}

function SubHead({ children }) {
  return <div className="text-xs uppercase tracking-[0.25em] font-bold text-asap-orange mt-5 mb-2 flex items-center gap-2">{children}</div>;
}

function KV({ k, v, sub, testid }) {
  return (
    <div className="border border-slate-200 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{k}</div>
      <div className="text-asap-deep font-mono text-sm break-all mt-1">{v || "—"}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Callout({ tone = "info", title, children }) {
  const tones = {
    info: "bg-blue-50 border-blue-400 text-blue-900",
    warn: "bg-amber-50 border-amber-500 text-amber-900",
    danger: "bg-red-50 border-asap-red text-asap-red",
  };
  return (
    <div className={`border-2 ${tones[tone]} p-3 my-3 text-sm`}>
      <div className="font-bold uppercase text-xs tracking-widest mb-1">{title}</div>
      <div>{children}</div>
    </div>
  );
}

function Badge({ tone, children }) {
  const tones = {
    danger: "bg-asap-red text-white",
    warn: "bg-amber-500 text-white",
    info: "bg-asap-deep text-white",
    ok: "bg-emerald-500 text-white",
  };
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${tones[tone] || tones.info}`}>{children}</span>;
}

function Playbook({ steps }) {
  return (
    <ol className="space-y-2">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3 text-sm">
          <span className="w-7 h-7 shrink-0 bg-asap-yellow text-asap-deep font-display grid place-items-center border-2 border-asap-deep">{i + 1}</span>
          <div>
            <div className="font-bold text-asap-deep">{s.t}</div>
            <div className="text-slate-600">{s.d}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

function StageCard({ stage, volume, hire, pay, cost }) {
  return (
    <div className="border-2 border-asap-deep p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest font-bold text-asap-orange">{stage}</div>
      <div className="font-display text-asap-deep text-lg">{cost}</div>
      <div className="text-xs text-slate-600 mt-1">{volume}</div>
      <div className="text-xs text-slate-700 mt-2 border-t pt-2"><strong>Who:</strong> {hire}</div>
      <div className="text-xs text-slate-700"><strong>How:</strong> {pay}</div>
    </div>
  );
}

function ChecklistItem({ status, title, note }) {
  const icons = {
    todo: <div className="w-4 h-4 border-2 border-slate-400 shrink-0"/>,
    pending: <Loader size={16} className="text-amber-600 shrink-0"/>,
    done: <CheckCircle2 size={16} className="text-emerald-600 shrink-0"/>,
    skip: <XCircle size={16} className="text-slate-400 shrink-0"/>,
  };
  return (
    <div className="flex items-start gap-3 py-2 border-b border-slate-100">
      <div className="mt-0.5">{icons[status]}</div>
      <div className="flex-1 text-sm">
        <div className={`font-bold ${status === "skip" ? "text-slate-500 line-through" : "text-asap-deep"}`}>{title}</div>
        <div className="text-xs text-slate-600 mt-0.5">{note}</div>
      </div>
    </div>
  );
}

function EndpointRow({ method, path, note }) {
  const colors = { GET: "text-emerald-700 bg-emerald-50", POST: "text-blue-700 bg-blue-50", PUT: "text-amber-700 bg-amber-50", DELETE: "text-asap-red bg-red-50" };
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 py-1">
      <span className={`px-1.5 py-0.5 text-[9px] font-bold tracking-widest ${colors[method] || "text-slate-700 bg-slate-50"}`}>{method}</span>
      <code className="text-asap-deep">{path}</code>
      <span className="text-slate-500 text-[10px] ml-auto">{note}</span>
    </div>
  );
}
